import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useTenant } from '@/contexts/TenantProvider'

export interface InstagramMediaItem {
  id: string
  media_type: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM'
  media_url: string
  thumbnail_url?: string
  caption?: string
  timestamp: string
  permalink: string
}

/**
 * Fetches Instagram media for the current tenant via the
 * `instagram-media` Supabase Edge Function.
 *
 * @see PERD v10.0 §4.5 — Social-First Sync Pipeline
 */
export function useInstagramMedia() {
  const { tenant } = useTenant()
  const tenantId = tenant?.id

  return useQuery<InstagramMediaItem[]>({
    queryKey: ['instagram-media', tenantId],
    queryFn: async () => {
      if (!tenantId) return []

      const { data: integration, error: integrationError } = await supabase
        .from('integrations_auth')
        .select('id')
        .eq('tenant_id', tenantId)
        .eq('provider', 'instagram')
        .maybeSingle()

      if (integrationError || !integration) {
        return []
      }

      const {
        data: { session },
      } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')

      let res: Response
      try {
        res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/instagram-media`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ tenant_id: tenantId }),
        })
      } catch {
        return []
      }

      if (!res.ok) {
        if (res.status === 404) return []
        const err = await res.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(err.error || `HTTP ${res.status}`)
      }

      const { data } = await res.json()
      return (data ?? []) as InstagramMediaItem[]
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 min — IG data doesn't change often
    retry: 1,
  })
}
